import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useBlocker } from 'react-router-dom';
import './useUnsavedChanges.css';

/**
 * Typed work is not thrown away without asking.
 *
 * The long forms - Create Ticket most of all, nine hundred lines of fields,
 * attachments and custom values - had no guard at all: a browser Back, a
 * link in the bell, a tab closed by mistake, and everything typed was gone.
 * The window-level drop guard in App.js exists because exactly that cost
 * Gordon a description once.
 *
 * Two doors are watched:
 *
 *   LEAVING THE PAGE (close, reload, another site): `beforeunload`, which is
 *   the browser's own box - no app can style it, and no app needs to.
 *
 *   LEAVING THE ROUTE (a link, the bell, Back): react-router's blocker, which
 *   holds the navigation while a small in-app dialog asks. "Stay" puts things
 *   back exactly as they were - Back included, which the router rewinds.
 *
 * `guard(fn)` is a third, for a form's own Cancel or a dialog's close button:
 * it runs `fn` at once when nothing has changed and asks first when something
 * has, through the same dialog. The form renders <UnsavedChangesDialog
 * leave={leave} /> once, anywhere; the hook does the rest.
 *
 * Needs the data router (App.js createBrowserRouter) - useBlocker does
 * nothing under a plain <BrowserRouter>.
 */
export default function useUnsavedChanges(dirty, { what = 'What you have typed' } = {}) {
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;
  const [asking, setAsking] = useState(null);   // { run } for guard(), or null

  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e) => {
      e.preventDefault();
      // Chrome wants returnValue set; the text is ignored by every browser.
      e.returnValue = '';
      return '';
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, [dirty]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) =>
    dirtyRef.current && currentLocation.pathname !== nextLocation.pathname);

  const guard = useCallback((run) => {
    if (!dirtyRef.current) { run(); return; }
    setAsking({ run });
  }, []);

  // The work was saved (or deliberately discarded): the next navigation is
  // free, at once - a saved form navigates to the thing it made in the same
  // tick, before any re-render could tell the blocker so.
  const release = useCallback(() => { dirtyRef.current = false; }, []);

  const open = blocker.state === 'blocked' || asking !== null;
  const leave = useCallback(() => {
    if (asking) { const { run } = asking; setAsking(null); run(); return; }
    if (blocker.state === 'blocked') blocker.proceed();
  }, [asking, blocker]);
  const stay = useCallback(() => {
    if (asking) { setAsking(null); return; }
    if (blocker.state === 'blocked') blocker.reset();
  }, [asking, blocker]);

  return { open, leave, stay, guard, release, what };
}

/** The small question. Rendered once by the form that owns the hook. */
export function UnsavedChangesDialog({ leave }) {
  const stayBtn = useRef(null);
  useEffect(() => {
    if (!leave.open) return undefined;
    const t = setTimeout(() => { if (stayBtn.current) stayBtn.current.focus(); }, 0);
    const onKey = (e) => { if (e.key === 'Escape') { e.stopPropagation(); leave.stay(); } };
    document.addEventListener('keydown', onKey, true);
    return () => { clearTimeout(t); document.removeEventListener('keydown', onKey, true); };
  }, [leave]);
  if (!leave.open) return null;
  return (
    <div className="unsaved-backdrop" role="presentation">
      <div className="unsaved-box" role="alertdialog" aria-modal="true"
           aria-labelledby="unsaved-title" aria-describedby="unsaved-text">
        <h2 className="unsaved-title" id="unsaved-title">Leave without saving?</h2>
        <p className="unsaved-text" id="unsaved-text">
          {leave.what} has not been saved and will be lost.
        </p>
        <div className="unsaved-actions">
          <button type="button" className="btn btn-secondary" ref={stayBtn} onClick={leave.stay}>
            Stay
          </button>
          <button type="button" className="btn btn-danger" onClick={leave.leave}>
            Leave and discard
          </button>
        </div>
      </div>
    </div>
  );
}
